import { UpdateMyProfileRequestDto } from "@/api/generated/generatedTypes";
import { ValidationError } from "@/api/types/errors";
import { UserSummaryDto } from "@/api/user/dto";
import { useUpdateMyProfile } from "@/api/user/useUpdateMyProfile";
import { hardcodedPersonalInfoQuestions } from "@/components/PersonalityTest/hardcoded-questions";
import { BirthdayInput } from "@/components/shared/BirthdayInput";
import { QuestionnaireFlowQuestion, QuestionnaireFlowQuestionOption } from "@/types/question.types";
import { formatDateToYYYYMMDD } from "@/utils/date";
import { Button, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";

export function EditUserProfileForm({ user }: { user: UserSummaryDto }) {
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateMyProfile();
  const form = useForm({
    initialValues: {
      name: user.name ?? "",
      nickname: user.nickname ?? "",
      gender: user.gender ?? "",
      maritalStatus: user.maritalStatus ?? "",
      hasChildren: user.hasChildren ?? "",
      profession: user.profession ?? "",
      nationality: user.nationality ?? "",
      birthDate: formatDateToYYYYMMDD(user.birthDate),
      groupGenderPreference: user.groupGenderPreference ?? "",
      groupAgePreference: user.groupAgePreference ?? "",
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    updateProfile(values as UpdateMyProfileRequestDto, {
      onSuccess: () => {
        notifications.show({
          title: "성공",
          message: "프로필이 성공적으로 업데이트되었습니다",
          color: "teal",
        });
      },
      onError: (error) => {
        if (error instanceof ValidationError) {
          if (error.messages) {
            // show stacked error message notifications
            error.messages.forEach((message) => {
              notifications.show({
                title: "입력 확인 필요",
                message: message,
                color: "orange",
                autoClose: 8000, // Longer time for multiple messages
              });
            });
            return;
          }
        }
        // Handle other API errors
        notifications.show({
          title: "오류",
          message: error.message || "프로필 업데이트 중 오류가 발생했습니다",
          color: "red",
        });
      },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput withAsterisk label="이름" placeholder="홍길동" {...form.getInputProps("name")} />
        <TextInput label="닉네임" placeholder="Gildong" {...form.getInputProps("nickname")} />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "gender")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.value ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="성별"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("gender")}
        />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "maritalStatus")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.value ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="연애 상태"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("maritalStatus")}
        />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "hasChildren")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.value ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="자녀"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("hasChildren")}
        />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "profession")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.value ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="직업/업종"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("profession")}
        />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "nationality")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.displayText ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="국적"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("nationality")}
        />
        <BirthdayInput label="생일" placeholder="YYYY-MM-DD" {...form.getInputProps("birthDate")} />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "groupGenderPreference")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.value ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="그룹 성별 선호도"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("groupGenderPreference")}
        />
        <Select
          data={
            hardcodedPersonalInfoQuestions
              .find((q: QuestionnaireFlowQuestion) => q.slug === "groupAgePreference")
              ?.extra?.options?.map((o: QuestionnaireFlowQuestionOption) => ({
                value: o.value ?? "",
                label: o.displayText,
              })) ?? []
          }
          label="그룹 연령 선호도"
          styles={{ option: { color: "black" } }}
          {...form.getInputProps("groupAgePreference")}
        />

        <Button type="submit" mt="md" loading={isUpdating}>
          프로필 업데이트
        </Button>
      </Stack>
    </form>
  );
}
